import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    Cog,
    Flame,
    MessagesSquare,
    Paintbrush,
    ScanLine
} from 'lucide-react'

import SectionHeading from '@/components/landing/section-heading'

interface Step {
    number: string
    title: string
    copy: string
    icon: LucideIcon
}

const STEPS: Step[] = [
    {
        number: '01',
        title: 'Consultation',
        copy: 'We begin with your car, how you drive it and the stance you want it to hold.',
        icon: MessagesSquare
    },
    {
        number: '02',
        title: 'Fitment mapping',
        copy: 'Hub, brake and fender measurements set the exact diameter, width and offset.',
        icon: ScanLine
    },
    {
        number: '03',
        title: 'Forging',
        copy: 'A single billet of 6061-T6 aluminum is forged into a dense monoblock blank.',
        icon: Flame
    },
    {
        number: '04',
        title: 'Machining',
        copy: 'Precision machining carves the deep concave face and the diamond-cut spokes.',
        icon: Cog
    },
    {
        number: '05',
        title: 'Hand finishing',
        copy: 'Brushing, polishing and coating by hand, then the signature cap is fitted and every wheel inspected.',
        icon: Paintbrush
    }
]

const CORNERS = [
    'left-4 top-4 border-l border-t',
    'right-4 top-4 border-r border-t',
    'bottom-4 left-4 border-b border-l',
    'bottom-4 right-4 border-b border-r'
]

const AtelierSection: FC = (): ReactNode => {
    return (
        <section id='atelier' className='relative overflow-hidden border-t border-white/5 bg-obsidian py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='grid items-center gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20'>
                    <div className='relative mx-auto aspect-square w-full max-w-md overflow-hidden rounded-xl border border-white/5 bg-slate-deep'>
                        <img
                            src='/images/atelier-wheel.jpg'
                            alt='Finished forged monoblock wheel standing on a dark workbench in the atelier, lit by a warm lamp'
                            width={1200}
                            height={1200}
                            loading='lazy'
                            decoding='async'
                            className='size-full object-cover'
                        />
                        <div className='pointer-events-none absolute inset-0 bg-linear-to-t from-obsidian/90 via-transparent to-obsidian/20' aria-hidden='true' />
                        {CORNERS.map((corner) => (
                            <span key={corner} className={`absolute size-5 border-gold/60 ${corner}`} aria-hidden='true' />
                        ))}
                        <p className='absolute bottom-7 left-0 right-0 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-titanium-light'>
                            Signature 10-spoke &middot; Deep concave
                        </p>
                    </div>

                    <div>
                        <SectionHeading
                            eyebrow='The atelier'
                            title={(
                                <>
                                    Alif Lamato.
                                    <br />
                                    <span className='text-gold-metal'>Wheel alchemist and architect.</span>
                                </>
                            )}
                        />
                        <div className='mt-8 space-y-5 text-base leading-relaxed text-titanium-light'>
                            <p>
                                Founder and master artisan Alif Lamato treats the wheel as the last thing designed on a car and the first thing anyone notices. Every set is drawn for one vehicle, then forged, machined and finished under the founder&apos;s own direction.
                            </p>
                            <p>
                                Nothing comes off a shelf. Diameter, width, offset, finish and engraving are decided together with you, so the result sits exactly where it should and looks as though it always belonged there.
                            </p>
                        </div>
                        <div className='mt-10 flex items-center gap-5 border-t border-white/5 pt-8'>
                            <span className='h-px w-10 bg-gold' aria-hidden='true' />
                            <div>
                                <p className='font-heading text-base font-bold tracking-[0.16em] text-white'>
                                    ALIF LAMATO
                                </p>
                                <p className='mt-1 font-mono text-[11px] uppercase tracking-[0.24em] text-titanium'>
                                    Founder and master artisan
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='mt-28'>
                    <SectionHeading
                        eyebrow='The process'
                        title='From your car to your set, in five steps.'
                        align='center'
                    />

                    <ol className='relative mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-5'>
                        <span className='pointer-events-none absolute left-0 right-0 top-[52px] hidden h-px bg-linear-to-r from-transparent via-gold/40 to-transparent lg:block' aria-hidden='true' />
                        {STEPS.map((step) => {
                            const Icon = step.icon

                            return (
                                <li key={step.number} className='relative rounded-xl border border-white/5 bg-slate-deep/70 p-7 transition-colors duration-300 hover:border-gold/30'>
                                    <div className='flex items-center justify-between'>
                                        <span className='flex size-12 items-center justify-center rounded-full border border-gold/40 bg-obsidian text-gold'>
                                            <Icon className='size-5' aria-hidden='true' />
                                        </span>
                                        <span className='font-heading text-sm font-bold text-titanium'>
                                            {step.number}
                                        </span>
                                    </div>
                                    <h3 className='mt-7 font-heading text-sm font-bold tracking-[0.12em] text-white'>
                                        {step.title.toUpperCase()}
                                    </h3>
                                    <p className='mt-3 text-sm leading-relaxed text-titanium-light'>
                                        {step.copy}
                                    </p>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </div>
        </section>
    )
}

export default AtelierSection
